import { useCallback, useEffect, useRef, useState } from 'react';
import { Disposition } from '../../types';
import { logger } from '../../utils/logger';
import { ACWResult } from './acw.types';

interface UseAcwCountdownOptions {
  duration: number;
  requireDisposition: boolean;
  selectedDispositionId: string;
  selectedDisposition: Disposition | undefined;
  notes: string;
  selectedSapContactId: string;
  onComplete: (result: ACWResult) => void | Promise<void>;
  onExtend?: (seconds: number) => void;
}

export function useAcwCountdown({
  duration,
  requireDisposition,
  selectedDispositionId,
  selectedDisposition,
  notes,
  selectedSapContactId,
  onComplete,
  onExtend,
}: UseAcwCountdownOptions) {
  const [remaining, setRemaining] = useState(duration);
  const [isExtending, setIsExtending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const hasSubmittedRef = useRef(false);

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const dispositionRequirementSatisfied = !requireDisposition || !!selectedDispositionId;
  const notesRequirementSatisfied = !selectedDisposition?.requiresNotes || notes.trim().length > 0;
  const canGoAvailable = dispositionRequirementSatisfied && notesRequirementSatisfied;

  const buildResult = useCallback((): ACWResult => {
    return {
      dispositionId: selectedDispositionId || undefined,
      dispositionName: selectedDisposition?.dispositionName,
      notes: notes.trim() || undefined,
      sapContactId: selectedSapContactId || undefined,
    };
  }, [selectedDispositionId, selectedDisposition, notes, selectedSapContactId]);

  const buildResultRef = useRef(buildResult);
  buildResultRef.current = buildResult;

  const submitResult = useCallback(async () => {
    if (hasSubmittedRef.current) {
      return;
    }

    hasSubmittedRef.current = true;
    const result = buildResultRef.current();
    logger.lifecycle('[ACW Flow] ACW timer submitting result', result);
    setIsSaving(true);

    try {
      await onCompleteRef.current(result);
    } catch (err) {
      hasSubmittedRef.current = false;
      logger.error('[ACWTimer] Save failed:', err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  useEffect(() => {
    setRemaining(duration);
    hasSubmittedRef.current = false;
  }, [duration]);

  useEffect(() => {
    if (duration <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [duration]);

  useEffect(() => {
    if (remaining > 0 || hasSubmittedRef.current || !canGoAvailable) {
      return;
    }

    void submitResult();
  }, [remaining, canGoAvailable, submitResult]);

  const handleGoAvailable = useCallback(async () => {
    await submitResult();
  }, [submitResult]);

  const handleExtend = useCallback(
    (additionalSeconds: number) => {
      setIsExtending(true);

      try {
        setRemaining((prev) => prev + additionalSeconds);
        onExtend?.(additionalSeconds);
      } finally {
        setIsExtending(false);
      }
    },
    [onExtend]
  );

  const isWarning = remaining <= 30;
  const progressPercent = duration > 0 ? (remaining / duration) * 100 : 0;

  return {
    remaining,
    isExtending,
    isSaving,
    canGoAvailable,
    isWarning,
    progressPercent,
    handleGoAvailable,
    handleExtend,
  };
}

export function formatAcwTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
